/**
 * PedirCotizacionMaterial — El vendedor pide precio de material desde su cotización.
 *
 * Antes esto era un correo suelto: el vendedor le escribía a un proveedor, guardaba (o no)
 * el PDF que le respondían, y en el ERP no quedaba nada. Cuando alguien preguntaba "¿a
 * quién le pediste precio?", la respuesta estaba en una casilla personal.
 *
 * Lo que crea es una SOLICITUD (`cotizando`), no una orden de compra: pedir precio no es
 * comprar. Queda ligada a esta cotización, así que después se puede caminar la cadena al
 * revés desde la factura del proveedor.
 *
 * El envío del correo NO se dispara desde acá. Lo hace Compras, con confirmación explícita
 * — escribirle a un proveedor desde la casilla oficial de la empresa no puede ser un
 * efecto secundario de apretar un botón en otra pantalla.
 */
import React, { useState } from "react";
import backend from "../../backend";

const campo = {
  background: "#111", border: "1px solid #2e2e2e", borderRadius: 5,
  padding: "5px 9px", color: "#ddd", fontSize: 12, width: "100%", boxSizing: "border-box",
};

const lineaVacia = () => ({ description: "", quantity: 1, unit: "un" });

const PedirCotizacionMaterial = ({ quotationId, onCreada }) => {
  const [abierto, setAbierto] = useState(false);
  const [lineas, setLineas] = useState([lineaVacia()]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [creada, setCreada] = useState(null);

  const set = (i, campoNombre, valor) =>
    setLineas(ls => ls.map((l, k) => (k === i ? { ...l, [campoNombre]: valor } : l)));

  const crear = async () => {
    const utiles = lineas.filter(l => l.description.trim());
    if (!utiles.length) { setError("Escribí al menos un material"); return; }
    setEnviando(true);
    setError("");
    try {
      const { data } = await backend.post("purchaseOrder/rfq-desde-cotizacion", {
        quotationId, items: utiles,
      });
      setCreada(data);
      setLineas([lineaVacia()]);
      onCreada?.(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setEnviando(false);
    }
  };

  if (!abierto) {
    return (
      <button type="button" onClick={() => setAbierto(true)}
        style={{ background: "#16a08522", color: "#16a085", border: "1px solid #16a08555",
                 borderRadius: 5, padding: "4px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
        📩 Pedir cotización de material
      </button>
    );
  }

  return (
    <div style={{ background: "#111", border: "1px solid #2e2e2e", borderRadius: 6, padding: 10, marginTop: 8 }}>
      <div style={{ fontSize: 11, color: "#8a8a8a", marginBottom: 8 }}>
        Se crea una solicitud de cotización ligada a esta cotización. El correo al proveedor
        lo manda Compras, confirmando el envío.
      </div>

      {lineas.map((l, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr", gap: 6, marginBottom: 5 }}>
          <input style={campo} placeholder="Material (ej: Barra SAE 1045 2&quot;)"
                 value={l.description} onChange={e => set(i, "description", e.target.value)} />
          <input style={campo} type="number" min="0" placeholder="Cant."
                 value={l.quantity} onChange={e => set(i, "quantity", e.target.value)} />
          <input style={campo} placeholder="un / m / kg"
                 value={l.unit} onChange={e => set(i, "unit", e.target.value)} />
        </div>
      ))}

      <button type="button" onClick={() => setLineas(ls => [...ls, lineaVacia()])}
        style={{ background: "none", border: "none", color: "#3498db", fontSize: 11, cursor: "pointer", padding: 0 }}>
        + otro material
      </button>

      {error && <div style={{ fontSize: 11, color: "#e74c3c", marginTop: 6 }}>⚠ {error}</div>}
      {creada && (
        <div style={{ fontSize: 11, color: "#2ecc71", marginTop: 6 }}>
          Solicitud N°{creada.number} creada. Está en Compras, esperando que le manden el correo al proveedor.
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <button type="button" onClick={crear} disabled={enviando}
          style={{ background: "#16a08522", color: "#16a085", border: "1px solid #16a08555",
                   borderRadius: 5, padding: "4px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
          {enviando ? "Creando…" : "Crear solicitud"}
        </button>
        <button type="button" onClick={() => { setAbierto(false); setCreada(null); setError(""); }}
          style={{ background: "none", color: "#8a8a8a", border: "1px solid #2e2e2e",
                   borderRadius: 5, padding: "4px 12px", fontSize: 11, cursor: "pointer" }}>
          Cerrar
        </button>
      </div>
    </div>
  );
};

export default PedirCotizacionMaterial;
